import { Spinner } from '@pypestream/design-system';
import { useRef } from 'react';
import { IPage } from '../types';
import { UserDetailsPage } from '../users/user-details/user-details-page';
import { resolveMyAccountTarget } from '../../utils';
import { useUserCtxSelector } from '../../xstate/app.xstate';

/**
 * The standalone My Account surface (PSTUDIO-4095).
 *
 * Before this existed, `/my-account` was a `<Navigate>` into
 * `/organization/:org_id/users/:user_id` — My Account was the Users route under
 * another URL. Removing the View Only floor (PRD: Manager Access Model,
 * Decision 3) takes the Users route away from a user with no org role, so the
 * two have to stop being the same surface.
 *
 * It shows the same sections the current user sees today: profile, Roles,
 * Agent Conversation Limit where it applies, and Login Methods. The only thing
 * it drops is the "All Users" back link, which points at a page this surface
 * exists to stop depending on. Delete User was already hidden for the current
 * user (`user-details.tsx`, `!isCurrentUser`).
 */
export const MyAccountPage = (props: IPage) => {
  const { loggedInUserId, loggedInUserDefaultOrgId } = useUserCtxSelector(
    (ctx) => ({
      loggedInUserId: ctx.user?.id,
      loggedInUserDefaultOrgId: ctx.user?.defaultAccount?.defaultOrgId,
    })
  );

  const target = resolveMyAccountTarget({
    loggedInUserId,
    loggedInUserDefaultOrgId,
  });

  // Hold the resolved pair across a refetch of the logged-in user.
  //
  // `user.refetch` is declared on `loggedIn`'s own `on` and targets `loggedIn`
  // (`user.xstate.ts`), i.e. an external self-transition, so its exit action
  // runs and clears `ctx.user` before the new profile arrives. Recomputing the
  // target from a momentarily empty `ctx.user` would swap the whole page for a
  // full-page spinner, unmounting `UserDetailsPage` and firing its cleanup:
  // `manager.userDetails.discardUpdates` (throwing away edits in flight) and
  // `manager.clearSelectedUser` (which primary-nav reads to decide whether to
  // refresh the user in the nav bar). The ids cannot change for a session, so
  // the last resolved pair stays valid, and `user-details.tsx` renders the
  // softer in-place indicator for this window via `isReloadingCurrentUser`.
  //
  // Reachability, measured rather than assumed: the only live sender is
  // `MainLayout`'s `onSuccess` for the onboarding modal, which is mounted in
  // the shared layout and so fires on this surface too. The manager machine
  // has a second sender in `reloadCurrentUser`, but no transition targets that
  // state — it is unreachable today, which is why saving a profile does NOT
  // currently trigger this path. Guarding here is cheap and survives that
  // state being wired up.
  const lastResolved = useRef<{ orgId: string; userId: string } | null>(null);
  if (target.ready) {
    lastResolved.current = { orgId: target.orgId, userId: target.userId };
  }

  const resolved = target.ready ? target : lastResolved.current;

  // Nothing resolved yet: a cold entry (a link from another app, or a reload
  // straight onto this URL) before the user machine has loaded the profile.
  if (!resolved) {
    return <Spinner fullpage />;
  }

  return (
    <UserDetailsPage
      {...props}
      userId={resolved.userId}
      orgId={resolved.orgId}
      isMyAccount
    />
  );
};
